import React, { Fragment } from 'react'
import classNames from 'classnames'

import { notify } from 'libs/common/notify'

import { EloInfoMessage } from '@elo-ui/components/elo-info-message'
import { EloWarningIcon } from '@elo-ui/components/icons/regular'

import { PAYMENT_STATES, ORDER_VIEW_TYPES } from 'constants/ordersShared.constants'
import { TRANSFER_STATES } from 'constants/transfersShared.constants'
import { getManagePageLink, withStores } from 'utils/helpers.utils'
import { getThemeProps } from '@elo-kit/utils/block.utils'
import { arrayLast } from 'utils/array.utils'

import { BLOCK_MENU_HIGHLIGHT_IDS } from 'shared/components/page-builder/constants/block.constants'
import { getSearchParams } from 'utils/queryString.utils'

import { PAYMENT_FORMS, PAYMENT_PROVIDERS } from 'constants/paymentSettingShared.constants'

import PaymentSuccess from './components/PaymentSuccess'
import { PaymentInProgress } from './components/PaymentInProgress'
import { RenamingEmailWarning } from './components/RenamingEmailWarning'
import PaymentError from './components/PaymentError'
import PaymentCanceled from './components/PaymentCanceled'
import InvoiceButton from './components/InvoiceButton'
import { useThankYouPageStyles } from './thankYouPageStyles'

const ThankYouPagePreview = (props) => {
  const {
    contentPageStore,
    contentPageStore: {
      user: { email },

      seller,
    },
    ordersStore: { item: data, list = [] },
    ordersStore = {},
    sellerStore,
    username,
    previewMode,
    block = {},
    experimentsStore,
  } = props

  const { currencyId, error } = data || {}

  const theme = getThemeProps(block)
  const classes = useThankYouPageStyles({ theme })

  const handleResendEmail = async (token) => {
    if (!previewMode) {
      await ordersStore.resendEmail(username, token)
      notify('success', I18n.t('react.shared.notify.success'), I18n.t('react.shared.notify.sent_email'))
    }
  }

  const redirectToManagePage = (token) => {
    if (!previewMode) {
      window.location.href = getManagePageLink(seller, token)
    }
  }

  const searchParams = getSearchParams()

  const showBankWireBoxFeatureGate = experimentsStore?.getFeatureGate('bankwire_incident_box')?.value

  return (
    <div className={classes.rootContainer}>
      <div className={classes.container}>
        {!!error && <PaymentError error={error} classes={classes} />}

        {!error && (
          <div
            {...(previewMode && {
              'data-highlighter-item': BLOCK_MENU_HIGHLIGHT_IDS.thankYouPage.header,
              'data-highlighter-selector': '',
            })}
            className={classes.successHeader}
          >
            {theme.headerText || I18n.t('react.shared.thank_you_for_your_order')}
          </div>
        )}

        {list.map((order, index) => {
          const {
            id,
            owner,
            payer,
            deal,
            paymentState,
            activatedAt,
            sendEmail,
            keepAccess,
            token,
            transfers,
            paymentForm,
            invoices,
            provider,
          } = order

          const isOwner = email === owner?.email || searchParams.from_email || owner?.email === payer?.email
          const isPaymentCancelled = paymentState === PAYMENT_STATES.paymentCanceled

          const isGift = owner?.id !== payer.id
          const viewType = isOwner ? ORDER_VIEW_TYPES.owner : ORDER_VIEW_TYPES.payer
          const showTransactionDetails = !isGift || viewType === ORDER_VIEW_TYPES.payer
          const lastTransfer = arrayLast(transfers)

          const hasDifferentBillingAddress = !!deal?.ownerData?.userProfileAttributes

          const commonProps = {
            classes,
            previewMode,
            block,
          }

          const props = {
            currencyId,
            order,
            viewType: isOwner ? ORDER_VIEW_TYPES.owner : ORDER_VIEW_TYPES.payer,
            isGift,
            hasDifferentBillingAddress,
            payerDataFromDeal: deal?.payerData?.userProfileAttributes,
            ownerDataFromDeal: deal?.ownerData?.userProfileAttributes,
            showTransactionDetails,
            lastTransfer,
            ...commonProps,
          }

          const isProductAccessable =
            activatedAt &&
            (paymentState === PAYMENT_STATES.paid ||
              paymentState === PAYMENT_STATES.paying ||
              paymentState === PAYMENT_STATES.paymentPaused ||
              keepAccess)
          const withDelayedAccess = !activatedAt && paymentState === PAYMENT_STATES.paying

          const isLastOrder = list.length === index + 1

          const oneTransfer =
            transfers.length === 1 ||
            transfers?.filter((transfer) => transfer.state === TRANSFER_STATES.successful).length === 1

          const isPayLater = paymentForm === PAYMENT_FORMS.payLater
          const invoiceLink = invoices?.length && arrayLast(invoices).downloadLink
          const payLaterInvoiceButton = !transfers.length && isPayLater && invoiceLink
          const isBankWire = paymentForm === PAYMENT_FORMS.bankWire
          const isAblefyConnectProvider = provider === PAYMENT_PROVIDERS.elopageConnect

          const showBankWireInfoMessage =
            showBankWireBoxFeatureGate &&
            isBankWire &&
            isAblefyConnectProvider &&
            !isProductAccessable &&
            !isPaymentCancelled

          return (
            <Fragment key={id}>
              <div className='thank-you-block'>
                {!sellerStore.item?.customSmtp && <RenamingEmailWarning />}
                {showBankWireInfoMessage && (
                  <div className='renaming-email-warning'>
                    <EloInfoMessage icon={<EloWarningIcon />} size='small' status='warning'>
                      {I18n.t('react.shared.payment.bank_wire_info_message')}
                    </EloInfoMessage>
                  </div>
                )}
                {withDelayedAccess && (
                  <div
                    className={classes.delayedAccess}
                    dangerouslySetInnerHTML={{
                      __html: I18n.t('react.shared.payment.access_delayed'),
                    }}
                  />
                )}

                {isProductAccessable ? (
                  <PaymentSuccess index={index} {...props} contentPageStore={contentPageStore} />
                ) : (
                  <PaymentInProgress
                    {...props}
                    isPaymentCancelled={isPaymentCancelled}
                    contentPageStore={contentPageStore}
                    ordersStore={ordersStore}
                    sellerStore={sellerStore}
                  />
                )}
                {isPaymentCancelled && !keepAccess && (
                  <PaymentCanceled redirectToManagePage={() => redirectToManagePage(token)} classes={classes} />
                )}
                <hr className={classes.lightSeparator} />

                <div className={classes.footer}>
                  <div className={classes.footerButtons}>
                    {oneTransfer && showTransactionDetails && (
                      <InvoiceButton invoiceLink={lastTransfer.invoiceLink} {...commonProps} />
                    )}
                    {!!payLaterInvoiceButton && <InvoiceButton invoiceLink={invoiceLink} {...commonProps} />}
                    <button
                      className={classNames(classes.additionalButton, 'higlight-secondary-button')}
                      onClick={() => redirectToManagePage(token)}
                      {...(previewMode && {
                        'data-highlighter-item': BLOCK_MENU_HIGHLIGHT_IDS.thankYouPage.secondaryButton,
                        'data-highlighter-selector': '.higlight-secondary-button',
                        'data-highlighter-parent': '.thank-you-block',
                      })}
                    >
                      <i className='fas fa-pencil-alt' />
                      <span>{I18n.t('react.shared.manage_page')}</span>
                    </button>
                  </div>

                  {!isPaymentCancelled && sendEmail && (
                    <div className={classNames(classes.footerInfo, 'higlight-details')}>
                      <span
                        {...(previewMode && {
                          'data-highlighter-item': BLOCK_MENU_HIGHLIGHT_IDS.thankYouPage.details,
                          'data-highlighter-selector': '.higlight-details',
                          'data-highlighter-parent': '.thank-you-block',
                        })}
                      >
                        {I18n.t('react.shared.resend_email')}
                      </span>
                      <button
                        className={classNames(classes.link, 'higlight-secondary-button')}
                        {...(previewMode && {
                          'data-highlighter-item': BLOCK_MENU_HIGHLIGHT_IDS.thankYouPage.secondaryButton,
                          'data-highlighter-selector': '.higlight-secondary-button',
                          'data-highlighter-parent': '.thank-you-block',
                        })}
                        onClick={() => handleResendEmail(token)}
                      >
                        {I18n.t('react.shared.button.resend_email')}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {!isLastOrder && <hr className={classes.bigSeparator} />}
            </Fragment>
          )
        })}
      </div>
    </div>
  )
}

export default withStores(['contentPageStore', 'ordersStore', 'sellerStore', 'experimentsStore'], ThankYouPagePreview)
